/**
 * B2B marketing nav — Glider-style mega-menu.
 * Platform dropdown (8 features, 2-col grid) + Solutions dropdown (4 ICPs).
 */

import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight,
  BarChart3,
  Building2,
  ChevronDown,
  Globe,
  LayoutDashboard,
  Mail,
  Menu,
  Moon,
  Shield,
  Sparkles,
  Sun,
  User,
  Video,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { GGJLogoMark } from '@shared/components/Brand/GGJLogo';
import { useAuth } from '@core/auth/useAuth';
import { useTheme } from '@contexts/ThemeContext';

export function b2cAppOrigin(): string {
  const fromEnv = import.meta.env.VITE_PUBLIC_B2C_APP_URL ?? import.meta.env.VITE_PUBLIC_B2C_URL;
  if (fromEnv && String(fromEnv).trim() !== '') return String(fromEnv).replace(/\/$/, '');
  return import.meta.env.DEV ? 'http://localhost:5173' : 'https://gograbjob.com';
}

type DropdownKey = 'platform' | 'solutions' | null;

const PLATFORM_FEATURES = [
  { label: 'Pipeline', desc: 'End-to-end ATS candidate tracking', icon: BarChart3, hash: '#solution' },
  { label: 'AI Interviews', desc: 'Structured live AI with rubric scoring', icon: Video, hash: '#solution' },
  { label: 'Explainable Role-Fit', desc: 'Weighted JD scoring — no black box', icon: Sparkles, hash: '#solution' },
  { label: 'Email Sequences', desc: 'Automated candidate nurture', icon: Mail, hash: '#solution' },
  { label: 'RBAC & Keycloak', desc: 'Enterprise role-based access control', icon: Shield, hash: '#features' },
  { label: 'Client Portal', desc: 'Dedicated agency client collaboration', icon: Building2, hash: '#features' },
  { label: 'Analytics', desc: 'Hiring funnel & performance metrics', icon: LayoutDashboard, hash: '#features' },
  { label: 'Multilingual', desc: 'AI interviews in 8+ languages', icon: Globe, hash: '#solution' },
] as const;

const SOLUTIONS = [
  {
    label: 'Recruitment & Staffing Agencies',
    desc: 'Client portal, submittal-ready narratives, RBAC',
    hash: '#who-its-for',
  },
  {
    label: 'In-house Talent Teams',
    desc: 'Employer mode, hiring-manager visibility, audit trail',
    hash: '#who-its-for',
  },
  {
    label: 'SMB & Growth Companies',
    desc: 'AI interviews, multilingual, fast role-fit screening',
    hash: '#who-its-for',
  },
  {
    label: 'Enterprise HR',
    desc: 'RBAC, compliance, full audit log, Keycloak SSO',
    hash: '#who-its-for',
  },
] as const;

export interface B2BMarketingNavProps {
  variant?: 'hero' | 'solid';
  anchorPrefix?: string;
  /** Hide the "Book Demo" CTA — use on the demo page itself so we don't link back to it. */
  hideDemoButton?: boolean;
}

export function B2BMarketingNav({ variant = 'hero', anchorPrefix = '', hideDemoButton = false }: B2BMarketingNavProps) {
  const [isScrolled, setIsScrolled] = useState(variant === 'solid');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<DropdownKey>(null);
  const [mobileSectionOpen, setMobileSectionOpen] = useState<DropdownKey>(null);
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const jobSeekerHref = b2cAppOrigin();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (variant === 'solid') return;
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [variant]);

  const handleSignIn = () => login('/dashboard');
  const scrolledLook = variant === 'solid' || isScrolled;

  function hrefFor(hash: string) {
    return anchorPrefix ? `${anchorPrefix}${hash}` : hash;
  }

  function openMenu(key: DropdownKey) {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDropdown(key);
  }

  function scheduleClose() {
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 90);
  }

  const barClass =
    variant === 'solid'
      ? 'sticky top-0 left-0 right-0 z-[100] pt-[env(safe-area-inset-top)] bg-white dark:bg-gray-900 shadow-md border-b border-gray-200 dark:border-gray-700'
      : `${isScrolled ? 'fixed top-0 left-0 right-0 z-[100] pt-[env(safe-area-inset-top)]' : 'relative'} w-full transition-all duration-300 ${
          isScrolled
            ? 'bg-white dark:bg-gray-900 shadow-md border-b border-gray-200 dark:border-gray-700'
            : 'bg-transparent'
        }`;

  const linkCls = `min-h-[44px] flex items-center gap-1 px-2 text-sm font-medium transition-colors touch-manipulation ${
    scrolledLook
      ? 'text-gray-700 dark:text-gray-300 hover:text-violet-600 dark:hover:text-violet-400'
      : 'text-white hover:text-white/85'
  }`;

  const panelBase =
    'absolute top-full mt-1.5 rounded-2xl border border-gray-200/80 bg-white shadow-xl ring-1 ring-black/5 dark:border-gray-700 dark:bg-gray-900 dark:ring-white/5 overflow-hidden';

  return (
    <>
      {variant === 'hero' && isScrolled && <div className="h-[52px] sm:h-16 shrink-0" aria-hidden />}

      <nav className={barClass}>
        <div className="relative mx-auto flex w-full max-w-[min(100%,1920px)] min-h-[52px] items-center justify-between gap-2 pl-1 pr-3 sm:h-16 sm:pl-2 sm:pr-4 lg:pl-3 lg:pr-8">
          {/* Logo */}
          <Link
            to="/business"
            className="flex shrink-0 min-h-[44px] items-center gap-2 hover:opacity-90 transition-opacity sm:gap-2.5"
          >
            <GGJLogoMark size={40} className="shrink-0" />
            <div className="leading-tight text-left">
              <span
                className={`block text-base font-bold tracking-tight sm:text-lg ${
                  scrolledLook ? 'text-gray-900 dark:text-white' : 'text-white'
                }`}
              >
                GoGrabJob
              </span>
              <span
                className={`block mt-0.5 text-[10px] font-semibold ${
                  scrolledLook ? 'text-violet-600 dark:text-violet-400' : 'text-violet-200'
                }`}
              >
                Business
              </span>
            </div>
          </Link>

          {/* ── Desktop center nav ── */}
          <div className="hidden flex-1 items-center justify-center gap-0 px-2 lg:flex xl:gap-1">
            {/* Platform dropdown */}
            <div
              className="relative"
              onMouseEnter={() => openMenu('platform')}
              onMouseLeave={scheduleClose}
            >
              <button className={linkCls}>
                Platform
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform duration-200 ${openDropdown === 'platform' ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>
              <AnimatePresence>
                {openDropdown === 'platform' && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.14 }}
                    className={`${panelBase} left-1/2 w-[592px] -translate-x-1/2`}
                    onMouseEnter={() => openMenu('platform')}
                    onMouseLeave={scheduleClose}
                  >
                    {/* Panel header */}
                    <div className="border-b border-gray-100 px-5 py-3 dark:border-gray-800">
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-600 dark:text-violet-400">
                        Platform
                      </p>
                      <p className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-white">
                        One workspace, end-to-end
                      </p>
                    </div>
                    {/* 2-col feature grid */}
                    <div className="grid grid-cols-2 gap-0 p-2">
                      {PLATFORM_FEATURES.map(({ label, desc, icon: Icon, hash }) => (
                        <a
                          key={label}
                          href={hrefFor(hash)}
                          onClick={() => setOpenDropdown(null)}
                          className="group flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-600 transition-colors group-hover:bg-violet-200 dark:bg-violet-900/40 dark:text-violet-400 dark:group-hover:bg-violet-900/60">
                            <Icon className="h-4 w-4" strokeWidth={1.75} />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-[13px] font-semibold text-gray-900 dark:text-white">
                              {label}
                            </p>
                            <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400">{desc}</p>
                          </div>
                        </a>
                      ))}
                    </div>
                    {/* Panel footer */}
                    <div className="flex items-center justify-between border-t border-gray-100 px-5 py-2.5 dark:border-gray-800">
                      <span className="text-[11px] text-gray-500 dark:text-gray-400">
                        Built for agencies &amp; in-house teams
                      </span>
                      <a
                        href={hrefFor('#features')}
                        onClick={() => setOpenDropdown(null)}
                        className="flex items-center gap-1 text-[12px] font-semibold text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300"
                      >
                        View all <ArrowRight className="h-3 w-3" />
                      </a>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Solutions dropdown */}
            <div
              className="relative"
              onMouseEnter={() => openMenu('solutions')}
              onMouseLeave={scheduleClose}
            >
              <button className={linkCls}>
                Solutions
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform duration-200 ${openDropdown === 'solutions' ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>
              <AnimatePresence>
                {openDropdown === 'solutions' && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.14 }}
                    className={`${panelBase} left-0 w-80`}
                    onMouseEnter={() => openMenu('solutions')}
                    onMouseLeave={scheduleClose}
                  >
                    <div className="border-b border-gray-100 px-5 py-3 dark:border-gray-800">
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-400">
                        Solutions
                      </p>
                      <p className="mt-0.5 text-sm font-semibold text-gray-900 dark:text-white">Who we serve</p>
                    </div>
                    <div className="p-2">
                      {SOLUTIONS.map(({ label, desc, hash }) => (
                        <a
                          key={label}
                          href={hrefFor(hash)}
                          onClick={() => setOpenDropdown(null)}
                          className="flex flex-col gap-0.5 rounded-xl px-3 py-2.5 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <p className="text-[13px] font-semibold text-gray-900 dark:text-white">{label}</p>
                          <p className="text-[11px] leading-snug text-gray-500 dark:text-gray-400">{desc}</p>
                        </a>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Direct section links */}
            <a href={hrefFor('#solution')} className={linkCls}>
              How it works
            </a>
            <a href={hrefFor('#who-its-for')} className={linkCls}>
              Who it's for
            </a>
            <a href={hrefFor('#features')} className={linkCls}>
              Platform
            </a>

            {/* Job Seekers */}
            <a
              href={jobSeekerHref}
              target="_blank"
              rel="noopener noreferrer"
              className={`ml-2 flex min-h-[44px] items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors touch-manipulation ${
                scrolledLook
                  ? 'border-violet-300 text-violet-600 hover:bg-violet-50 dark:border-violet-600 dark:text-violet-400 dark:hover:bg-violet-900/20'
                  : 'border-white/40 text-white hover:bg-white/10'
              }`}
            >
              <User className="h-4 w-4 shrink-0" aria-hidden />
              Job Seekers
            </a>
          </div>

          {/* ── Desktop right actions ── */}
          <div className="hidden shrink-0 items-center gap-1 lg:flex sm:gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 transition-colors ${
                scrolledLook
                  ? 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                  : 'text-white hover:bg-white/10'
              }`}
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <button
              type="button"
              onClick={handleSignIn}
              className={`min-h-[44px] rounded-lg px-3 py-2 text-sm font-medium transition-colors sm:px-4 ${
                scrolledLook
                  ? 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                  : 'text-white hover:bg-white/10'
              }`}
            >
              Login
            </button>
            {!hideDemoButton && (
              <Link
                to="/business/demo"
                className={`flex min-h-[44px] items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                  scrolledLook
                    ? 'border-2 border-violet-500 text-violet-600 hover:bg-violet-50 dark:border-violet-500 dark:text-violet-400 dark:hover:bg-violet-900/20'
                    : 'border border-white/30 text-white hover:bg-white/10'
                }`}
                style={
                  scrolledLook
                    ? undefined
                    : { background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)' }
                }
              >
                Book Demo
                <ArrowRight className="h-4 w-4" />
              </Link>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 transition-colors lg:hidden ${
              scrolledLook
                ? 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                : 'text-white hover:bg-white/10'
            }`}
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </nav>

      {/* ── Mobile slide-over ── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-sm lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.25 }}
              className="fixed bottom-0 right-0 top-0 z-[120] flex w-full max-w-sm flex-col overflow-y-auto bg-white shadow-xl dark:bg-gray-900 lg:hidden"
            >
              <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
                <span className="text-sm font-bold text-gray-900 dark:text-white">GoGrabJob Business</span>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-3 py-3">
                {/* Platform accordion */}
                <div>
                  <button
                    onClick={() =>
                      setMobileSectionOpen(mobileSectionOpen === 'platform' ? null : 'platform')
                    }
                    className="flex w-full min-h-[44px] items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                  >
                    Platform
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${mobileSectionOpen === 'platform' ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {mobileSectionOpen === 'platform' && (
                    <div className="mb-2 ml-2 mt-1 flex flex-col gap-0.5">
                      {PLATFORM_FEATURES.map(({ label, desc, icon: Icon, hash }) => (
                        <a
                          key={label}
                          href={hrefFor(hash)}
                          onClick={() => setMobileOpen(false)}
                          className="flex min-h-[44px] items-center gap-3 rounded-md px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-400">
                            <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-[13px] font-medium text-gray-800 dark:text-gray-100">{label}</p>
                            <p className="truncate text-[10px] text-gray-500 dark:text-gray-400">{desc}</p>
                          </div>
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                {/* Solutions accordion */}
                <div>
                  <button
                    onClick={() =>
                      setMobileSectionOpen(mobileSectionOpen === 'solutions' ? null : 'solutions')
                    }
                    className="flex w-full min-h-[44px] items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                  >
                    Solutions
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${mobileSectionOpen === 'solutions' ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {mobileSectionOpen === 'solutions' && (
                    <div className="mb-2 ml-2 mt-1 flex flex-col gap-0.5">
                      {SOLUTIONS.map(({ label, hash }) => (
                        <a
                          key={label}
                          href={hrefFor(hash)}
                          onClick={() => setMobileOpen(false)}
                          className="flex min-h-[44px] items-center rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800"
                        >
                          {label}
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                <a
                  href={hrefFor('#solution')}
                  onClick={() => setMobileOpen(false)}
                  className="flex min-h-[44px] items-center rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  How it works
                </a>
                <a
                  href={hrefFor('#who-its-for')}
                  onClick={() => setMobileOpen(false)}
                  className="flex min-h-[44px] items-center rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  Who it's for
                </a>
                <a
                  href={hrefFor('#features')}
                  onClick={() => setMobileOpen(false)}
                  className="flex min-h-[44px] items-center rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  Platform
                </a>
                <a
                  href={jobSeekerHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  className="flex min-h-[44px] items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-violet-600 hover:bg-violet-50 dark:text-violet-400 dark:hover:bg-violet-900/20"
                >
                  <User className="h-4 w-4 shrink-0" aria-hidden />
                  Job Seekers
                </a>
              </div>

              {/* Mobile footer */}
              <div className="flex flex-col gap-2 border-t border-gray-200 px-3 py-4 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    toggleTheme();
                  }}
                  className="flex w-full min-h-[44px] items-center justify-center gap-2 rounded-lg bg-gray-100 py-2.5 text-sm font-medium text-gray-900 dark:bg-gray-700 dark:text-white"
                >
                  {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  Theme
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    handleSignIn();
                  }}
                  className="w-full min-h-[44px] rounded-lg bg-gray-100 py-2.5 text-center text-sm font-medium text-gray-900 hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                >
                  Login
                </button>
                {!hideDemoButton && (
                  <Link
                    to="/business/demo"
                    onClick={() => setMobileOpen(false)}
                    className="flex w-full min-h-[44px] items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold text-white"
                    style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #8b80f9 100%)' }}
                  >
                    Book Demo <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
